import React, { useCallback } from 'react'
import classNames from 'classnames'
import CarouselNav from '@/atoms/CarouselNav'
import { ChatLinesIcon } from '@/atoms/Icons/ChatLinesIcon'
import { FireOutlineIcon } from '@/atoms/Icons/FireOutlineIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { MegaphoneIcon } from '@/atoms/Icons/MegaphoneIcon'
import { Image } from '@/atoms/Image'
import { EyebrowLG, HeadingMD, ParagraphMD } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useCarousel from '@/utils/useCarousel'
import useWindowSize from '@/utils/useWindowSize'
import { TorchSubmitButton } from '../ TorchSubmitButton'
import { TorchStepsCard } from '../TorchStepsCard'

export const TorchHeroSection: React.FC = () => {
  const [scrollerRef, { canScrollBack, canScrollForward, scrollBack, scrollForward }] = useCarousel<HTMLDivElement>()
  const { track } = useSafeAnalytics()
  const { innerWidth } = useWindowSize()
  const { t } = useTranslate('create')

  const iconSize = innerWidth > 1024 ? 48 : 32

  const stepCards = [
    {
      icon: <FireOutlineIcon size={iconSize} color="white" />,
      title: t('oneSubmitProject', '1. Submit Your Project'),
      description: t(
        'submitProjectDescription',
        'We accept submissions for completed films and series, as well as concept videos (Torches).',
      ),
    },
    {
      icon: <GuildIconMono size={iconSize} color="white" />,
      title: t('guildVoting', '2. Guild Voting'),
      description: t(
        'guildStepTwoDescription',
        'Your submission is voted on by the Angel Guild to determine if it qualifies for Angel partnership.',
      ),
    },
    {
      icon: <ChatLinesIcon size={iconSize} color="white" />,
      title: t('angelReview', '3. Angel Review'),
      description: t(
        'angelReviewDescription',
        'Once you pass the Guild, we meet with you to discuss a potential partnership with Angel.',
      ),
    },
    {
      icon: <MegaphoneIcon size={iconSize} color="white" />,
      title: t('distribution', '4. Distribution'),
      description: t(
        'distributionDescription',
        'After reaching an agreement, we work together to share your project with the world.',
      ),
    },
  ]

  const handleSubmitClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section className="w-full py-5 md:py-12 lg:py-20">
      <div className="flex w-full max-w-[1440px] flex-col lg:flex-row lg:justify-between 3lg:mx-auto 3lg:gap-[250px]">
        <div className="px-4 md:px-12 lg:max-w-[794px] lg:px-[120px] 2xl:pr-[80px] 3lg:w-auto 3lg:max-w-[900px] 3lg:px-0">
          <div className="mb-6 flex w-full flex-col gap-3 md:mb-12 md:gap-6 se:mb-3 se:gap-2 3lg:min-w-[600px]">
            <HeadingMD
              weight="bold"
              color="white"
              className="se:photon-title-md md:photon-heading-xl 2xl:photon-display"
            >
              {t('shapeFutureEntertainment', 'Shape The Future of Entertainment With Your Project')}
            </HeadingMD>
            <ParagraphMD
              weight="medium"
              color="core-gray-400"
              className="se:photon-caption-sm md:photon-paragraph-lg xl:photon-paragraph-xl 2xl:max-w-auto 2xl:max-w-[470px]"
            >
              {t(
                'lookingNextBigHitEntertainment',
                "We're looking for the next big hit in entertainment that amplifies light to the world.",
              )}
            </ParagraphMD>
          </div>
          <div className="gap-3 space-y-4 md:flex md:space-y-0">
            <TorchSubmitButton handleSubmitClicked={handleSubmitClicked} />
          </div>
        </div>
        <Image
          alt={t('torchesThumbnails', 'Torches Thumbnails')}
          className="w-full md:hidden"
          src={getCloudinaryImageUrl({
            path: '/v1717167539/angel-studios/create/Frame_1261158169.png',
          })}
          width={428}
          height={314}
        />
        <Image
          alt={t('torchesThumbnails', 'Torches Thumbnails')}
          className="hidden md:block lg:max-w-[600px] xl:max-w-[700px] 3lg:hidden"
          src={getCloudinaryImageUrl({
            path: '/v1717168037/angel-studios/create/Frame_1261158168_1.png',
          })}
          width={886}
          height={568}
        />
        <Image
          alt={t('torchesThumbnails', 'Torches Thumbnails')}
          className="hidden max-w-[650px] 2xl:max-w-[700px] 3lg:block 3xl:max-w-[900px]"
          src={getCloudinaryImageUrl({
            path: '/v1718049702/angel-studios/create/Frame_1261158168_2.png',
          })}
          width={1026}
          height={568}
        />
      </div>
      <div className="max-w-[1440px] lg:mt-4 2xl:mt-8 3lg:mx-auto">
        <EyebrowLG
          weight="semibold"
          color="core-gray-500"
          className="se:photon-eyebrow-md px-4 uppercase md:px-12 lg:px-[120px] 3lg:px-0"
        >
          {t('steps', 'Steps')}
        </EyebrowLG>
        <div className="relative mt-4 w-full md:mt-6 se:mt-2">
          <div
            className="relative flex w-full max-w-[1440px] snap-x snap-mandatory flex-row gap-4 overflow-scroll px-4 scrollbar-none md:flex-wrap md:px-12 lg:flex-nowrap lg:px-[120px] 3lg:mx-auto 3lg:px-0"
            ref={scrollerRef}
          >
            {stepCards.map((card) => {
              return <TorchStepsCard key={card.title} {...card} />
            })}
          </div>
          <CarouselNav
            onClick={scrollBack}
            active={canScrollBack}
            orientation="horizontal"
            direction="back"
            className={classNames('ml-4', { 'opacity-75 hover:opacity-100': canScrollBack })}
            theme="dark"
          />
          <CarouselNav
            onClick={scrollForward}
            active={canScrollForward}
            orientation="horizontal"
            direction="forward"
            className={classNames('mr-4', { 'opacity-75 hover:opacity-100': canScrollForward })}
            theme="dark"
          />
        </div>
      </div>
    </section>
  )
}
